import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { useConfirmationModalStore } from "../store/zustand/DependencyConfirmationModal";
import { X, Loader2 } from "lucide-react";
import { memo } from "react";

interface DependencyConfirmationModalProps {
  files: string[];
  dependencies: string[];
  onConfirm: () => Promise<void>;
  onReject: () => void;
  isOpen: boolean;
  isProcessing: boolean;
}

export const DependencyConfirmationModal = ({
  files,
  dependencies,
  onConfirm,
  onReject,
  isOpen,
  isProcessing,
}: DependencyConfirmationModalProps) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  const hideModal = useConfirmationModalStore((state) => state.hideModal);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      const isProcessing = useConfirmationModalStore.getState().isProcessing;
      if (!isProcessing) {
        hideModal();
      }
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="bg-[#1C1C1C] border-0 text-white gap-4 p-4">
        <AlertDialogHeader className="relative">
          <AlertDialogTitle className="text-sm">
            Install packages
          </AlertDialogTitle>
          <button
            onClick={onReject}
            className="text-text-secondary hover:text-text-primary absolute -right-0 -top-2"
            disabled={isProcessing}
          >
            <X size={16} />
          </button>

          <AlertDialogDescription className="text-xs">
            You're importing a React component that needs extra tools in order
            to work. Add these to your project by installing them.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {dependencies.length > 0 && (
          <>
            <div className="text-gray-400 text-xs">Packages to install</div>
            <div className="mb-4">
              <div className="bg-background-secondary rounded p-2 text-text-primary font-mono text-xs">
                {dependencies.map((dependency) => (
                  <div key={dependency}>{dependency}</div>
                ))}
              </div>
            </div>
          </>
        )}

        {files.length > 0 && (
          <>
            <div className="text-gray-400 text-xs">New Shadcn files</div>
            <div className="mb-4">
              <div className="bg-background-secondary rounded p-2 text-text-primary font-mono text-xs">
                {files.map((file) => (
                  <div key={file}>{file}</div>
                ))}
              </div>
            </div>
          </>
        )}

        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            className="bg-background-secondary text-text-primary border-0 hover:bg-background-secondary/80 text-xs"
            onClick={onReject}
            disabled={isProcessing}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className="bg-primary-0 text-text-primary hover:bg-background-primary/80 text-xs"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                Installing...
              </span>
            ) : (
              "Install"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const DependencyConfirmationModalWrapper = memo(() => {
  const isOpen = useConfirmationModalStore((state) => state.isOpen);
  const files = useConfirmationModalStore((state) => state.files);
  const dependencies = useConfirmationModalStore((state) => state.dependencies);
  const onConfirm = useConfirmationModalStore((state) => state.onConfirm);
  const onReject = useConfirmationModalStore((state) => state.onReject);
  const isProcessing = useConfirmationModalStore((state) => state.isProcessing);

  if (!onConfirm) return null;
  return (
    <DependencyConfirmationModal
      files={files}
      dependencies={dependencies}
      onConfirm={onConfirm}
      onReject={onReject}
      isOpen={isOpen}
      isProcessing={isProcessing}
    />
  );
});
